'use client'
import HotelDateModifier from '@/src/components/common/hotels/review/hotelDateModifier'
import FullPageLoader from '@/src/components/common/ui/fullPageLoader'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import useCreateHotelItinerary from '@/src/hooks/hotels/useCreateHotelItinerary'
import useHotelFailureDialog from '@/src/hooks/hotels/useHotelFailureDialog'
import { ReviewTab } from '@/src/components/mobile/hotels/review/reviewTabBar'
import { HotelReviewViewModel } from '@/src/types/hotels/details'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useCallback, useState } from 'react'
import HotelDetailsMobile from './details'

/**
 * What a page must supply to render hotel details. The booking flow provides it from
 * useHotelReviewData; the property (SEO) page from usePropertyPageData. The two load
 * differently — one arrives priced from a search, the other from Google with no dates —
 * but everything below this point is identical, so it lives here once.
 */
export interface HotelDetailsDataSource {
    viewModel: HotelReviewViewModel | null
    isLoading: boolean
    isError: boolean
    isUpdatingDates: boolean
    /** Runs the search directly. The booking flow arrives priced and has none. */
    searchRooms?: () => void
    applyDateChange: (checkIn: Date, checkOut: Date) => Promise<boolean>
}

export interface HotelDetailsMobileScreenProps {
    locale: string
    domainCountry?: string
    data: HotelDetailsDataSource
    /** Presentation the property (SEO) page needs; the booking flow passes none of it. */
    availableTabs?: ReviewTab[]
    defaultTab?: ReviewTab
    expandAll?: boolean
    searchLabel?: string
    /** Rooms-and-guests control. Only the property (SEO) page supplies one. */
    guestsField?: React.ReactNode
    breadcrumb?: React.ReactNode
    /** Let the guest apply the stay already on screen — the property page has not priced it. */
    allowUnchangedDates?: boolean
}

const HotelDetailsMobileScreen = ({
    locale,
    domainCountry,
    data,
    availableTabs,
    defaultTab,
    expandAll,
    searchLabel,
    guestsField,
    breadcrumb,
    allowUnchangedDates
}: HotelDetailsMobileScreenProps) => {
    const t = useTranslations('hotels')
    const router = useRouter()
    // Only `searchAgain` is needed here; the dialog itself belongs to the action steps.
    const { searchAgain } = useHotelFailureDialog(locale, false)
    const { viewModel, isLoading, isError, isUpdatingDates, applyDateChange } = data

    const [pickerOpen, setPickerOpen] = useState(false)

    const handleBack = useCallback(() => {
        router.back()
    }, [router])

    // Close the picker before the overlay goes up — one blocking layer at a time.
    const handleApplyDates = useCallback(
        (checkIn: Date, checkOut: Date) => {
            setPickerOpen(false)
            void applyDateChange(checkIn, checkOut)
        },
        [applyDateChange]
    )

    // Selecting a rate creates the Cleartrip itinerary and hands off its id; the id is the
    // whole address of the booking, so nothing travels in the query string.
    const { selectRoom: handleSelectRoom, isCreating } = useCreateHotelItinerary({
        viewModel,
        locale,
        domainCountry: domainCountry ?? ''
    })

    return (
        <>
            <ErrorBoundaryWrapper identifier="hotel-details-mobile">
                <HotelDetailsMobile
                    availableTabs={availableTabs}
                    defaultTab={defaultTab}
                    expandAll={expandAll}
                    searchLabel={searchLabel}
                    onSearch={data.searchRooms}
                    guestsField={guestsField}
                    breadcrumb={breadcrumb}
                    viewModel={viewModel}
                    isLoading={isLoading}
                    isError={!isLoading && (isError || !viewModel)}
                    errorTitle={t('reviewErrorTitle')}
                    errorSubtitle={t('reviewErrorSubtitle')}
                    errorCtaLabel={t('searchAgain')}
                    tabLabels={{
                        rooms: t('selectRoomTab'),
                        info: t('hotelInfoTab'),
                        map: t('viewOnMapTab')
                    }}
                    photosCountLabel={count => t('photosCount', { count })}
                    viewPhotosLabel={t('viewPhotos')}
                    onEditDates={viewModel ? () => setPickerOpen(true) : undefined}
                    datePicker={
                        viewModel && (
                            <HotelDateModifier
                                variant="mobile"
                                open={pickerOpen}
                                stayCheckIn={viewModel.stay.checkIn}
                                stayCheckOut={viewModel.stay.checkOut}
                                onClose={() => setPickerOpen(false)}
                                onApply={handleApplyDates}
                                allowUnchanged={allowUnchangedDates}
                            guestsField={guestsField}
                            />
                        )
                    }
                    onBack={isError ? searchAgain : handleBack}
                    onSelectRoom={handleSelectRoom}
                />
            </ErrorBoundaryWrapper>
            {(isUpdatingDates || isCreating) && <FullPageLoader message={t('pleaseWait')} />}
        </>
    )
}

export default HotelDetailsMobileScreen
